import { DataTable } from '@/components/data-table/DataTable';
import { TableFilter } from '@/components/data-table/TableFilter';
import { PageSize } from '@/components/pagination/PageSize';
import { PAGE_SIZES } from './list/constants';
import { LpohPagination } from '@/components/pagination/LpohPagination';
import useTableFilter from '@/components/data-table/useTableFilter';
import { ColumnDef } from '@tanstack/react-table';
import { ProcessObjectFile } from '../process-objects/list/types';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Params, SortDirection } from '@/utils/types';
import { TableHeaderWithSort } from '@/components/data-table/TableHeaderWithSort';
import { getPaginatedItems } from '@/utils/get-paginated-items.ts';
import { format } from 'date-fns';
import { DEFAULT_DATE_TIME_FORMAT } from '@/utils/dates';
import { DEFAULT_PAGE_SIZE } from '../calibration-task/list/constants';
import { Button } from '@/components/ui/button';
import { UploadIcon } from '@radix-ui/react-icons';
import { Eye, Move3dIcon, Pencil } from 'lucide-react';
import { maybeCellValue } from '@/utils/maybe-cell-value';
import { toast } from 'sonner';
import axios from 'axios';
import { useMutation } from '@tanstack/react-query';
import { fetchProcessObjectFile, queryGeometry } from './constants';
import Spinner from '@/components/spinner/Spinner';
import { getUIErrorMessage } from '@/utils/errors';
import ErrorAlert from '@/components/error-alert/ErrorAlert';
import ProcessObjectFileUpload from './po-file-upload/ProcessObjectFileUpload';
import ProcessObjectFileUpdateModal from './po-file-upload/ProcessObjectFileUpdateModal';
import { mergeQueryParams } from '@/utils/merge-query-params';
import { getMimeTypeFromFilename, isViewableFile } from './file-view-utils';

type ProcessObjectFiles = {
	measurementGroupId: number;
	measurementGroupName: string;
	files: ProcessObjectFile[];
};

const IS_UPLOAD_FORM_OPEN_PARAM = 'isUploadFormOpen';

const FILE_NOT_AVAILABLE_MESSAGE =
	'File not available in storage. The file record exists but the object could not be found.';

async function parseDownloadErrorResponse(
	data: Blob | string | Record<string, unknown>
): Promise<string | null> {
	try {
		let body: { errorMessage?: string; errorCode?: string };
		if (data instanceof Blob) {
			body = JSON.parse(await data.text());
		} else if (typeof data === 'string') {
			body = JSON.parse(data);
		} else {
			body = data as { errorMessage?: string; errorCode?: string };
		}
		if (body?.errorCode === 'FILE_NOT_AVAILABLE') return FILE_NOT_AVAILABLE_MESSAGE;
		return body?.errorMessage ?? null;
	} catch {
		return null;
	}
}

async function downloadProcessObjectFile(
	processObjectId: number,
	fileId: number,
	filename: string
): Promise<void> {
	try {
		const response = await fetchProcessObjectFile({ processObjectId, fileId });
		const objectUrl = URL.createObjectURL(response.data);
		const a = document.createElement('a');
		a.href = objectUrl;
		a.download = filename || 'download';
		a.click();
		URL.revokeObjectURL(objectUrl);
	} catch (err) {
		let message = 'Download failed.';
		if (axios.isAxiosError(err) && err.response?.data != null) {
			const parsedMessage = await parseDownloadErrorResponse(err.response.data);
			if (parsedMessage) message = parsedMessage;
		} else if (err instanceof Error) {
			message = err.message;
		}
		throw new Error(message);
	}
}

const BLOB_URL_REVOKE_DELAY_MS = 60_000;

type ViewProcessObjectFileArgs = {
	processObjectId: number;
	fileId: number;
	filename: string;
	previewWindow: Window;
};

function writePreviewLoadingState(previewWindow: Window) {
	previewWindow.document.open();
	previewWindow.document.write(`
        <!doctype html>
        <html lang="en">
            <head>
                <title>Loading preview...</title>
            </head>
            <body style="font-family: sans-serif; padding: 16px;">
                Loading preview...
            </body>
        </html>
    `);
	previewWindow.document.close();
}

function closePreviewWindow(previewWindow?: Window | null) {
	if (!previewWindow || previewWindow.closed) return;
	try {
		previewWindow.close();
	} catch {
		// ignore close errors
	}
}

async function viewProcessObjectFile({
	processObjectId,
	fileId,
	filename,
	previewWindow
}: ViewProcessObjectFileArgs): Promise<void> {
	try {
		const response = await fetchProcessObjectFile({ processObjectId, fileId });
		const mimeType = getMimeTypeFromFilename(filename);
		const blob = new Blob([response.data], { type: mimeType });
		const objectUrl = URL.createObjectURL(blob);
		previewWindow.location.replace(objectUrl);
		setTimeout(() => {
			URL.revokeObjectURL(objectUrl);
		}, BLOB_URL_REVOKE_DELAY_MS);
	} catch (err) {
		closePreviewWindow(previewWindow);
		let message = 'Failed to open file.';
		if (axios.isAxiosError(err) && err.response?.data != null) {
			const parsedMessage = await parseDownloadErrorResponse(err.response.data);
			if (parsedMessage) message = parsedMessage;
		} else if (err instanceof Error) {
			message = err.message;
		}
		throw new Error(message);
	}
}

const openGeometryTab = (html: string) => {
	const win = window.open();

	if (!win) return;

	win.document.open();
	win.document.write(html);
	win.document.close();
};

type GeometryButtonProps = {
	processObjectId: number;
	fileId: number;
};

function GeometryButton({ processObjectId, fileId }: GeometryButtonProps) {
	const { mutate: fetchGeometry, isPending: isGeometryLoading } = useMutation({
		mutationFn: queryGeometry,
		onSuccess: openGeometryTab,
		onError: error => {
			const err = getUIErrorMessage(error);
			toast(
				<ErrorAlert variant="ghost" className="p-0" title="Failed to get site geometry." description={err} />
			);
		}
	});

	return (
		<Button variant="ghost" disabled={isGeometryLoading} onClick={() => fetchGeometry({ processObjectId, fileId })}>
			{isGeometryLoading ? <Spinner size={18} /> : <Move3dIcon size={18} />}
		</Button>
	);
}

type FileDownloadButtonProps = {
	processObjectId: number;
	fileId: number;
	name: string;
};

function FileDownloadButton({ processObjectId, fileId, name }: FileDownloadButtonProps) {
	const { mutate: triggerDownload, isPending: isDownloading } = useMutation({
		mutationFn: () => downloadProcessObjectFile(processObjectId, fileId, name),
		onError: error => {
			const message = error instanceof Error ? error.message : getUIErrorMessage(error);
			toast(
				<ErrorAlert variant="ghost" className="p-0" title="Download failed" description={message} />
			);
		}
	});

	return (
		<button
			type="button"
			className="hover:text-gray-400 cursor-pointer text-left underline bg-transparent border-0 p-0 font-inherit"
			onClick={e => {
				e.preventDefault();
				triggerDownload();
			}}
			disabled={isDownloading}
			title="Download file"
		>
			{isDownloading ? <Spinner size={16} /> : name}
		</button>
	);
}

type FileViewButtonProps = {
	processObjectId: number;
	fileId: number;
	name: string;
};

function FileViewButton({ processObjectId, fileId, name }: FileViewButtonProps) {
	const { mutate: triggerView, isPending: isViewing } = useMutation({
		mutationFn: (variables: ViewProcessObjectFileArgs) => viewProcessObjectFile(variables),
		onError: (error, variables) => {
			closePreviewWindow(variables.previewWindow);
			const message = error instanceof Error ? error.message : getUIErrorMessage(error);
			toast(
				<ErrorAlert variant="ghost" className="p-0" title="Failed to open file" description={message} />
			);
		}
	});

	const handleView = () => {
		const previewWindow = window.open('', '_blank');
		if (!previewWindow) {
			toast(
				<ErrorAlert
					variant="ghost"
					className="p-0"
					title="Failed to open file"
					description="Popup blocked. Allow popups to preview files."
				/>
			);
			return;
		}
		try {
			previewWindow.opener = null;
		} catch {
			// ignore opener assignment errors
		}
		writePreviewLoadingState(previewWindow);
		triggerView({
			processObjectId,
			fileId,
			filename: name,
			previewWindow
		});
	};

	return (
		<Button
			type="button"
			variant="ghost"
			size="icon"
			className="h-8 w-8 shrink-0"
			onClick={handleView}
			disabled={isViewing}
			title="View file"
			aria-label={`View file ${name}`}
		>
			{isViewing ? <Spinner size={18} /> : <Eye className="h-4 w-4" />}
		</Button>
	);
}

const ProcessObjectFiles = ({ measurementGroupName, files }: ProcessObjectFiles) => {
	const { handleFilterChange } = useTableFilter();
	const [searchParams] = useSearchParams();
	const [fileToUpdate, setFileToUpdate] = useState<ProcessObjectFile | null>(null);

	const navigate = useNavigate();

	const isUploadFormOpen = Boolean(searchParams.get(IS_UPLOAD_FORM_OPEN_PARAM)) || false;

	const page = searchParams.get(Params.page) || '1';
	const limit = searchParams.get(Params.pageSize) || DEFAULT_PAGE_SIZE;
	const sortBy = (searchParams.get(Params.sortBy) || 'createdAt') as keyof ProcessObjectFile;
	const sortDirection = (searchParams.get(Params.sortDirection) || SortDirection.DESC) as SortDirection;
	const search = searchParams.get(Params.search) || '';

	const processObjectFilesFilter = (item: ProcessObjectFile, searchValue: string) => {
		return (
			item.name.toLowerCase().includes(searchValue) ||
			item.title.toLowerCase().includes(searchValue) ||
			item.description.toLowerCase().includes(searchValue) ||
			format(new Date(item.createdAt), DEFAULT_DATE_TIME_FORMAT).includes(searchValue) ||
			item.processObjectId.toString().toLowerCase().includes(searchValue)
		);
	};

	const toggleFileUploadForm = () => {
		const queryParams = mergeQueryParams(searchParams, {
			[IS_UPLOAD_FORM_OPEN_PARAM]: isUploadFormOpen ? undefined : 'true'
		});

		navigate(`?${queryParams}`);
	};

	const { paginatedItems: paginatedFiles, totalPages } = getPaginatedItems<ProcessObjectFile>({
		items: files,
		search,
		page,
		limit,
		sortBy,
		sortDirection,
		filterPredicate: processObjectFilesFilter
	});
	const columns: ColumnDef<ProcessObjectFile>[] = [
		{
			accessorKey: 'title',
			header: ({ column }) => <TableHeaderWithSort column={column} columnName="Title" />
		},
		{
			accessorKey: 'processObjectId',
			header: ({ column }) => <TableHeaderWithSort column={column} columnName="Process object ID" />
		},
		{
			accessorKey: 'description',
			header: ({ column }) => <TableHeaderWithSort column={column} columnName="Description" />
		},
		{
			accessorKey: 'userId',
			header: 'User',
			cell: ({ row }) => maybeCellValue(row.original.userId.toString())
		},
		{
			accessorKey: 'createdAt',
			header: ({ column }) => <TableHeaderWithSort column={column} columnName="Created at" />,
			cell: ({ row }) => format(new Date(row.original.createdAt), DEFAULT_DATE_TIME_FORMAT)
		},
		{
			accessorKey: 'name',
			header: ({ column }) => <TableHeaderWithSort column={column} columnName="File name" />,
			cell: ({ row }) => (
				<div className="flex items-center gap-2">
					<FileDownloadButton
						processObjectId={row.original.processObjectId}
						fileId={row.original.id}
						name={row.original.name}
					/>
					{isViewableFile(row.original.name) && (
						<FileViewButton
							processObjectId={row.original.processObjectId}
							fileId={row.original.id}
							name={row.original.name}
						/>
					)}
					<Button
						type="button"
						variant="ghost"
						size="icon"
						className="h-8 w-8 shrink-0"
						onClick={() => setFileToUpdate(row.original)}
						title="Update file"
						aria-label={`Update file ${row.original.name}`}
					>
						<Pencil className="h-4 w-4" />
					</Button>
				</div>
			)
		},
		{
			accessorKey: 'geometry',
			header: 'Geometry',
			cell: ({row}) => (
				<GeometryButton processObjectId={row.original.processObjectId} fileId={row.original.id}/>
			)
		}
	];
	return (
		<div className="flex flex-col gap-5 pt-5">
			<p className="font-bold">Process object files</p>
			<div className="w-full flex gap-5 flex-col">
				<Button className="w-fit" onClick={toggleFileUploadForm}>
					<UploadIcon/>
					<span>Upload</span>
				</Button>

				<TableFilter handleFilterChange={handleFilterChange} />

				<DataTable columns={columns} data={paginatedFiles} isLoading={false} />

				<div className="flex justify-between">
					<PageSize pageSize={limit} options={PAGE_SIZES} />
					<LpohPagination currentPage={parseInt(page)} totalPages={totalPages} />
				</div>
			</div>
			<ProcessObjectFileUpload isOpen={isUploadFormOpen} onClose={toggleFileUploadForm} />
			<ProcessObjectFileUpdateModal
				isOpen={fileToUpdate != null}
				onClose={() => setFileToUpdate(null)}
				measurementGroupName={measurementGroupName}
				file={fileToUpdate}
			/>
		</div>
	);
};

export default ProcessObjectFiles;
